import { useRef, useState } from "react";
import {
  Cloud,
  CloudOff,
  DatabaseBackup,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  Laptop,
  LogOut,
  RefreshCw,
  Server,
  ShieldCheck,
  Smartphone,
  Upload
} from "lucide-react";
import clsx from "clsx";
import type { WorkRecord, WorkbenchBackup } from "../types";
import { createBackup, restoreBackup, saveRecord } from "../lib/db";
import {
  csvTemplate,
  downloadBlob,
  exportJson,
  exportMarkdown,
  parseBackup,
  parseCsvImport,
  type CsvImportPreview
} from "../lib/export";
import {
  Button,
  ConfirmDialog,
  PageHeader
} from "../components/ui";

export interface SyncState {
  mode: "local" | "syncing" | "synced" | "offline" | "error";
  lastSyncedAt?: string;
  message?: string;
}

export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function SettingsView({
  records,
  syncState,
  authenticated,
  installPrompt,
  onSync,
  onLogout,
  notify
}: {
  records: WorkRecord[];
  syncState: SyncState;
  authenticated: boolean;
  installPrompt: InstallPromptEvent | null;
  onSync: () => void;
  onLogout: () => void;
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const jsonInput = useRef<HTMLInputElement>(null);
  const csvInput = useRef<HTMLInputElement>(null);
  const [pendingBackup, setPendingBackup] = useState<WorkbenchBackup>();
  const [csvPreview, setCsvPreview] = useState<CsvImportPreview>();
  const [importError, setImportError] = useState("");

  async function exportAllJson() {
    exportJson(await createBackup());
    notify("完整 JSON 备份已生成");
  }

  async function readJsonFile(file?: File) {
    if (!file) return;
    try {
      setImportError("");
      setPendingBackup(parseBackup(await file.text()));
    } catch (error) {
      notify(error instanceof Error ? error.message : "无法读取备份文件", "error");
    } finally {
      if (jsonInput.current) jsonInput.current.value = "";
    }
  }

  async function readCsvFile(file?: File) {
    if (!file) return;
    try {
      setImportError("");
      setCsvPreview(parseCsvImport(await file.text()));
    } catch {
      setImportError("无法读取这个 CSV 文件。");
    } finally {
      if (csvInput.current) csvInput.current.value = "";
    }
  }

  async function confirmCsvImport() {
    if (!csvPreview) return;
    for (const record of csvPreview.records) {
      await saveRecord(record);
    }
    notify(`已导入 ${csvPreview.records.length} 条记录`);
    setCsvPreview(undefined);
  }

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
  }

  return (
    <div className="view">
      <PageHeader
        eyebrow="设置与数据"
        title="你的工作台，你的数据"
        description="本地记录始终可以打开；云端负责跨设备同步。任何 AI 结果都需要你确认后才写入。"
      />

      <div className="settings-grid">
        <section className="surface settings-section">
          <div className="settings-heading">
            <div className="settings-icon"><Cloud size={21} /></div>
            <div>
              <p className="eyebrow">同步</p>
              <h2>{authenticated ? "私有云同步已连接" : "当前为本地模式"}</h2>
            </div>
            <span className={clsx("connection-dot", syncState.mode)} />
          </div>
          <p className="settings-copy">
            {authenticated
              ? "记录先写入本机，再安全同步到你的私有 Cloudflare 数据空间。断网时仍可继续工作。"
              : "云端尚未连接，电脑上的记录会保存在浏览器 IndexedDB 中。配置并部署云端后即可手机同步。"}
          </p>
          <div className="sync-detail-grid">
            <div><span>当前状态</span><strong>{syncLabel(syncState.mode)}</strong></div>
            <div><span>最近同步</span><strong>{syncState.lastSyncedAt ? new Date(syncState.lastSyncedAt).toLocaleString("zh-CN") : "尚未同步"}</strong></div>
          </div>
          {syncState.message && <p className="inline-message">{syncState.message}</p>}
          <div className="settings-actions">
            {authenticated && (
              <Button type="button" onClick={onSync}>
                <RefreshCw size={16} /> 立即同步
              </Button>
            )}
            {authenticated && (
              <Button type="button" variant="ghost" onClick={onLogout}>
                <LogOut size={16} /> 退出云端
              </Button>
            )}
          </div>
        </section>

        <section className="surface settings-section">
          <div className="settings-heading">
            <div className="settings-icon"><DatabaseBackup size={21} /></div>
            <div>
              <p className="eyebrow">备份与迁移</p>
              <h2>随时带走完整数据</h2>
            </div>
          </div>
          <p className="settings-copy">建议每周导出一次 JSON 备份。它包含任务、思考、决策、每日聚焦和复盘。</p>
          <div className="backup-actions">
            <button type="button" onClick={() => void exportAllJson()}>
              <FileJson size={19} />
              <span><strong>完整 JSON 备份</strong><small>用于恢复全部数据</small></span>
              <Download size={16} />
            </button>
            <button type="button" onClick={() => exportMarkdown(records)}>
              <FileText size={19} />
              <span><strong>可读 Markdown</strong><small>适合长期阅读与归档</small></span>
              <Download size={16} />
            </button>
            <button type="button" onClick={() => downloadBlob("任务导入模板.csv", csvTemplate(), "text/csv;charset=utf-8")}>
              <FileSpreadsheet size={19} />
              <span><strong>CSV 导入模板</strong><small>从空库开始，按需导入</small></span>
              <Download size={16} />
            </button>
          </div>
          <div className="settings-actions">
            <input
              ref={jsonInput}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={(event) => void readJsonFile(event.target.files?.[0])}
            />
            <Button type="button" onClick={() => jsonInput.current?.click()}>
              <Upload size={16} /> 恢复 JSON 备份
            </Button>
            <input
              ref={csvInput}
              type="file"
              accept="text/csv,.csv"
              hidden
              onChange={(event) => void readCsvFile(event.target.files?.[0])}
            />
            <Button type="button" onClick={() => csvInput.current?.click()}>
              <Upload size={16} /> 导入 CSV
            </Button>
          </div>
          {importError && <p className="form-error">{importError}</p>}
        </section>

        <section className="surface settings-section">
          <div className="settings-heading">
            <div className="settings-icon"><Smartphone size={21} /></div>
            <div>
              <p className="eyebrow">手机使用</p>
              <h2>把工作台装到桌面</h2>
            </div>
          </div>
          <p className="settings-copy">
            iPhone 用 Safari 打开部署地址，点击分享按钮，再选择“添加到主屏幕”。手机语音使用系统键盘自带的听写。
          </p>
          <div className="device-diagram">
            <div><Laptop size={20} /><span>Mac</span></div>
            <div className="sync-line"><Cloud size={17} /></div>
            <div><Smartphone size={20} /><span>iPhone</span></div>
          </div>
          {installPrompt ? (
            <Button type="button" variant="primary" onClick={() => void installApp()}>
              <Download size={16} /> 安装到这台设备
            </Button>
          ) : (
            <p className="muted">当前浏览器没有提供自动安装入口，可继续按上面的方式手动添加。</p>
          )}
        </section>

        <section className="surface settings-section security-section">
          <div className="settings-heading">
            <div className="settings-icon"><ShieldCheck size={21} /></div>
            <div>
              <p className="eyebrow">边界</p>
              <h2>知道什么会去哪里</h2>
            </div>
          </div>
          <div className="security-list">
            <div><Server size={18} /><p><strong>云端数据</strong>保存在你配置的 Cloudflare D1 私有空间中，使用 HTTPS 和单用户口令保护。</p></div>
            <div><CloudOff size={18} /><p><strong>断网时</strong>全部记录仅写入本机，恢复联网后再同步。</p></div>
            <div><ShieldCheck size={18} /><p><strong>语音整理</strong>在 Cloudflare AI 内完成；DeepSeek 只接收你明确选中并确认发送的内容，不会后台读取工作台。</p></div>
          </div>
          <p className="settings-footnote">最高敏感信息建议暂不录入，或在使用 AI 时明确排除。服务提供方仍按各自的数据政策管理加密存储。</p>
        </section>
      </div>

      {pendingBackup && (
        <ConfirmDialog
          title="恢复这份备份？"
          description={`备份导出于 ${new Date(pendingBackup.exportedAt).toLocaleString("zh-CN")}，包含 ${pendingBackup.records.length} 条记录。恢复会替换当前本机数据，但不会删除云端历史。`}
          confirmLabel="确认恢复"
          onClose={() => setPendingBackup(undefined)}
          onConfirm={() => {
            void restoreBackup(pendingBackup).then((count) => {
              setPendingBackup(undefined);
              notify(`已恢复 ${count} 条记录`);
            });
          }}
        />
      )}

      {csvPreview && (
        <ConfirmDialog
          title="导入预览"
          description={`识别到 ${csvPreview.records.length} 条可导入记录${csvPreview.errors.length ? `，另有 ${csvPreview.errors.length} 行需要跳过：${csvPreview.errors.slice(0, 3).join("；")}` : "，没有格式错误。"}`}
          confirmLabel="开始导入"
          onClose={() => setCsvPreview(undefined)}
          onConfirm={() => {
            if (csvPreview.records.length > 0) void confirmCsvImport();
          }}
        />
      )}
    </div>
  );
}

function syncLabel(mode: SyncState["mode"]): string {
  return {
    local: "本地模式",
    syncing: "正在同步",
    synced: "已同步",
    offline: "离线，等待恢复",
    error: "同步遇到问题"
  }[mode];
}
